/**
 * External store for removed side edge features — red ghost display.
 * Keyed by composite key `{edge}:{featureId}`.
 * Entries persist until the worker finishes recomputing geometry.
 */
import type {SideEdge, SideEdgeFeatureConfig} from '../types'

/** Shape data captured at the moment of removal */
export interface RemovedSideEdgeGhost {
  type: SideEdgeFeatureConfig['type']
  edge: SideEdge
  position: number
  width: number
  height: number
  cornerRadius: number
  diameter: number
  /** Side button protrude depth (signed offset of outer face from device surface) */
  depth: number
  /** Side button chamfer at the cutout entrance (mm) */
  chamfer: number
}

let snapshot: Record<string, RemovedSideEdgeGhost> | null = null
const listeners = new Set<() => void>()

const notify = () => listeners.forEach((l) => l())

/**
 * Record a removed side edge feature for red ghost display.
 * @param edge — edge name (left/right/top/bottom)
 * @param id — feature ID
 * @param ghost — full shape data at the time of removal
 */
export const setRemovedSideEdge = (edge: SideEdge, id: string, ghost: RemovedSideEdgeGhost) => {
  const key = `${edge}:${id}`
  snapshot = {...snapshot, [key]: ghost}
  notify()
}

export const clearRemovedSideEdge = () => {
  snapshot = null
  notify()
}

export const getRemovedSideEdgeSnapshot = (): Record<string, RemovedSideEdgeGhost> | null => snapshot

export const subscribeRemovedSideEdge = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
